import React, { useState, useEffect } from "react";
import { Button, Card, Col, Dropdown, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import { RevenueCharts } from "./DashboardCharts";
import CountUp from "react-countup";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

//import images
import bodyLight from "assets/images/sidebar/body-light-1.png";
import CustomDropdownToggle from "Common/CustomDropdownToggle";

import { getChartData as getChartApiData } from "../../slices/thunk";
import { dashboardData } from "api/apis";

const Revenue = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [chartData, setchartData] = useState<any>([]);
  const [dashboardStats, setDashboardStats] = useState<any>({
    orderCount: 0,
    totalEarnings: 0,
    userCount: 0,
    refunds: 0,
  });

  const [activeChart, setactiveChart] = useState<string>("yearly");

  const selectProperties = createSelector(
    (state: any) => state.Dashboard,
    (dashboard) => ({
      revenueChartData: dashboard.chartData,
    })
  );

  const { revenueChartData } = useSelector(selectProperties);

  // Enhanced transform function - properly structures data for ApexCharts
  const transformDataForChart = (yearlyMonthlyData: any[]) => {
    // ALWAYS ensure we have exactly 12 months of data (Jan to Dec)
    const months = Array.from({ length: 12 }, (_, i) => {
      const monthData = yearlyMonthlyData[i] || {
        earnings: 0,
        orders: 0,
        users: 0,
      };
      return {
        earnings: Math.round(monthData.earnings || 0),
        orders: Math.round(monthData.orders || 0),
        users: Math.round(monthData.users || 0),
      };
    });

    const earnings = months.map((item) => item.earnings);
    const orders = months.map((item) => item.orders);
    const users = months.map((item) => item.users);

    console.log("Chart Data Debug - All 3 Series:", {
      seriesCount: 3,
      earnings: { data: earnings, total: earnings.reduce((a, b) => a + b, 0) },
      orders: { data: orders, total: orders.reduce((a, b) => a + b, 0) },
      users: { data: users, total: users.reduce((a, b) => a + b, 0) },
      monthsCount: earnings.length,
    });

    // Return all 3 series - MUST be in this exact order
    return [
      {
        name: "Earnings",
        data: earnings,
        type: "line",
      },
      {
        name: "Orders",
        data: orders,
        type: "line",
      },
      {
        name: "Users",
        data: users,
        type: "line",
      },
    ];
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      const response = await dashboardData();
      if (response.success) {
        const { data } = response;

        // Set dashboard stats
        setDashboardStats({
          orderCount: Math.round(data.orderCount || 0),
          totalEarnings: Math.round(data.totalEarnings || 0),
          userCount: Math.round(data.userCount || 0),
          refunds: Math.round(data.userCount || 0),
        });

        // Transform and set chart data
        if (data.yearlyMonthlyData && data.yearlyMonthlyData.length > 0) {
          const transformedData = transformDataForChart(data.yearlyMonthlyData);
          setchartData(transformedData);
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (revenueChartData && revenueChartData.length > 0) {
      setchartData(revenueChartData);
    }
  }, [revenueChartData]);

  const onChangeChartPeriod = (pType: any) => {
    dispatch(getChartApiData(pType));
    setactiveChart(pType);
  };

  useEffect(() => {
    dispatch(getChartApiData("yearly"));
    setactiveChart("yearly");
  }, [dispatch]);

  return (
    <React.Fragment>
      <Col xs={12}>
        <Card>
          <Card.Header className="align-items-center d-flex flex-wrap justify-content-between">
            <h4 className="card-title mb-0 flex-grow-1">Revenue</h4>
          </Card.Header>

          <Card.Body>
            <Row>
              {/* Chart Section */}
              <Col xs={12} xl={8}>
                <RevenueCharts
                  chartData={chartData}
                  dataColors='["--tb-success", "--tb-primary", "--tb-danger"]'
                  fillType="none"
                />
              </Col>

              {/* Stats Section */}
              <Col xs={12} xl={4}>
                <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-3 mb-4 mt-3 mt-xl-0">
                  {/* <div className="input-group">
                    <Flatpickr
                      className="form-control flatpickr-input"
                      options={{
                        mode: "range",
                        dateFormat: "d M, Y",
                        defaultDate: ["01 Jan 2023", "31 Jan 2023"],
                      }}
                    />
                    <div className="input-group-text bg-primary border-primary text-white">
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div> */}
                  <Dropdown className="flex-shrink-0">
                    <Dropdown.Toggle
                      as={CustomDropdownToggle}
                      onClick={() => navigate("/reports")}
                      className="text-reset dropdown-btn d-inline-block"
                    >
                      <span className="text-muted">Reports</span>
                    </Dropdown.Toggle>
                  </Dropdown>
                </div>

                <Row className="g-0 text-center">
                  {[
                    {
                      label: "Orders",
                      value: dashboardStats.orderCount,
                      iconColor: "text-primary",
                      icon: "ri-shopping-cart-line",
                    },
                    {
                      label: "Earnings",
                      value: dashboardStats.totalEarnings,
                      prefix: "₹",
                      iconColor: "text-success",
                      icon: "ri-money-rupee-circle-line",
                    },
                    {
                      label: "Users",
                      value: dashboardStats.userCount,
                      iconColor: "text-danger",
                      icon: "ri-group-line",
                    },
                    // {
                    //   label: "Products",
                    //   value:
                    //     dashboardStats.orderCount > 0 &&
                    //     dashboardStats.userCount > 0
                    //       ? Math.round(
                    //           (dashboardStats.orderCount /
                    //             dashboardStats.userCount) *
                    //             100
                    //         )
                    //       : 0,
                    //   suffix: "%",
                    //   className: "text-success",
                    //   iconColor: "text-info",
                    //   icon: "ri-percent-line",
                    // },
                  ].map((stat, index) => (
                    <Col xs={6} key={index}>
                      <div
                        className={`p-3 border border-dashed ${
                          index % 2 !== 0 ? "border-start-0" : ""
                        } ${index < 2 ? "border-bottom-0" : ""}`}
                      >
                        <h5 className={`mb-1`}>
                          {/* {stat.prefix || ""} */}
                          <CountUp
                            start={0}
                            end={stat.value}
                            separator=","
                            decimals={0}
                          />
                          {/* {stat.suffix || ""} */}
                        </h5>
                        <p className="text-muted mb-0">{stat.label}</p>
                      </div>
                    </Col>
                  ))}
                </Row>

                <Card className="mt-4 mb-0 bg-info-subtle border-0">
                  <Card.Body>
                    <h5 className="fs-16">
                      Reached {dashboardStats.userCount} Customers
                    </h5>
                    <p className="text-muted">
                      Hey! Awesome products! Can you share the best product
                      name?
                    </p>
                    <Link to="/users-list" className="btn btn-info btn-sm">
                      See User's List{" "}
                      <i className="bi bi-arrow-right ms-1 align-middle"></i>
                    </Link>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default Revenue;
